import React, { useState, useEffect, useCallback } from 'react';
import { Fab, Badge, Tooltip, Zoom } from '@mui/material';
import { AttachMoney } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import { intlLocale } from '../i18n/i18n';
import './CashFloatingWidget.css';

const CashFloatingWidget = ({ onClick, refreshTrigger }) => {
  const { t } = useTranslation('sell');
  const { getAccessToken } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const response = await fetch(`${config.API_BASE_URL}/em/v1/volunteer/cash-summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data) {
          setSummary(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching cash summary:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchSummary();
      setPulse(true);
      const timeout = setTimeout(() => setPulse(false), 1500);
      return () => clearTimeout(timeout);
    }
  }, [refreshTrigger, fetchSummary]);

  const netAmount = summary?.netCashExpected ?? 0;
  const transactionCount = summary?.totalTransactions ?? 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(intlLocale(), {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Zoom in>
      <div className={`cash-floating-widget ${pulse ? 'cash-floating-widget--pulse' : ''}`}>
        <Tooltip
          title={
            <div className="cash-widget-tooltip">
              <div className="cash-widget-tooltip__title">{t('cash.widget.title')}</div>
              <div className="cash-widget-tooltip__amount">{formatCurrency(netAmount)}</div>
              <div className="cash-widget-tooltip__detail">
                {t('cash.widget.transactions', { count: transactionCount })}
              </div>
              {summary?.totalRefunded > 0 && (
                <div className="cash-widget-tooltip__refund">
                  {t('cash.widget.refunded', { amount: formatCurrency(summary.totalRefunded) })}
                </div>
              )}
              <div className="cash-widget-tooltip__hint">{t('cash.widget.hint')}</div>
            </div>
          }
          placement="left"
          arrow
        >
          <Badge
            badgeContent={transactionCount > 0 ? transactionCount : null}
            color="primary"
            max={99}
          >
            <Fab
              color={netAmount > 0 ? 'success' : 'default'}
              onClick={onClick}
              className="cash-floating-widget__fab"
              size="medium"
              disabled={loading}
            >
              <AttachMoney />
            </Fab>
          </Badge>
        </Tooltip>
        {netAmount > 0 && (
          <div className="cash-floating-widget__label" onClick={onClick}>
            {formatCurrency(netAmount)}
          </div>
        )}
      </div>
    </Zoom>
  );
};

export default CashFloatingWidget;
